import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { Empty } from 'antd';
import type { DeclarationCase, CaseStatus } from '@/api/cases';
import {
  CASE_STATUS_LABEL,
  CASE_STATUS_ORDER,
  CASE_STATUS_VISUAL,
} from '@/api/cases';
import type { Role } from '@/lib/role';
import { CaseCard } from './CaseCard';

/**
 * 7 列 Kanban 看板 — 固定列顺序：draft → frozen → submitted → reviewing → approved / rejected / withdrawn。
 *
 * 数据来源：useQuery(['cases', filters]) 已拉满，本组件仅按 status 分桶。
 * 不做拖拽（状态机推进必须经后端守卫，前端拖拽 UX 误导风险高 → 用按钮显式触发）。
 */
export interface KanbanBoardProps {
  cases: DeclarationCase[];
  role: Role | null;
  pendingId?: string | null; // 正在 mutation 的 case_id（按钮 loading）
  onFreeze: (c: DeclarationCase) => void;
  onSubmit: (c: DeclarationCase) => void;
  onStartReview: (c: DeclarationCase) => void;
  onRecordReview: (c: DeclarationCase) => void;
  onWithdraw: (c: DeclarationCase) => void;
  onDelete: (c: DeclarationCase) => void;
  onGenerateArtifacts: (c: DeclarationCase) => void;
  /** S20-C0:打开指派 / 改派 Modal */
  onAssign?: (c: DeclarationCase) => void;
  /** S20-C1:打开案工作台 /cases/:id */
  onView?: (c: DeclarationCase) => void;
}

interface Bucket {
  status: CaseStatus;
  items: DeclarationCase[];
}

function bucketize(cases: DeclarationCase[]): Bucket[] {
  const map = new Map<CaseStatus, DeclarationCase[]>();
  for (const s of CASE_STATUS_ORDER) map.set(s, []);
  for (const c of cases) {
    const arr = map.get(c.status);
    if (arr) arr.push(c);
  }
  return CASE_STATUS_ORDER.map((status) => ({
    status,
    items: map.get(status) ?? [],
  }));
}

const columnStyle: CSSProperties = {
  flex: '0 0 280px',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--color-bg-page-warm)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-2)',
  minHeight: 360,
  maxHeight: 'calc(100vh - 260px)',
};

const columnHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '6px 10px 10px',
  borderBottom: '1px dashed var(--color-border-soft)',
  marginBottom: 'var(--space-2)',
};

export function KanbanBoard({
  cases,
  role,
  pendingId,
  onFreeze,
  onSubmit,
  onStartReview,
  onRecordReview,
  onWithdraw,
  onDelete,
  onGenerateArtifacts,
  onAssign,
  onView,
}: KanbanBoardProps) {
  const buckets = useMemo(() => bucketize(cases), [cases]);

  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-3)',
        overflowX: 'auto',
        paddingBottom: 8,
      }}
    >
      {buckets.map(({ status, items }) => {
        const visual = CASE_STATUS_VISUAL[status];
        return (
          <div key={status} style={columnStyle}>
            <div style={columnHeaderStyle}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 'var(--font-size-sm)',
                  fontWeight:
                    'var(--font-weight-semi)' as CSSProperties['fontWeight'],
                  color: 'var(--color-text-primary)',
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 'var(--radius-full)',
                    background: visual.fg,
                  }}
                />
                {CASE_STATUS_LABEL[status]}
              </span>
              <span
                style={{
                  background: visual.bg,
                  color: visual.fg,
                  borderRadius: 'var(--radius-pill)',
                  padding: '0 8px',
                  fontSize: 'var(--font-size-xs)',
                  lineHeight: '18px',
                  minWidth: 22,
                  textAlign: 'center',
                }}
              >
                {items.length}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
                overflowY: 'auto',
                flex: 1,
                padding: '0 2px 6px',
              }}
            >
              {items.length === 0 ? (
                <div
                  style={{
                    padding: 'var(--space-4) 0',
                    textAlign: 'center',
                  }}
                >
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-tertiary)',
                        }}
                      >
                        暂无
                      </span>
                    }
                    styles={{ image: { height: 36 } }}
                  />
                </div>
              ) : (
                items.map((c) => (
                  <CaseCard
                    key={c.id}
                    case_={c}
                    role={role}
                    pending={pendingId === c.id}
                    onFreeze={onFreeze}
                    onSubmit={onSubmit}
                    onStartReview={onStartReview}
                    onRecordReview={onRecordReview}
                    onWithdraw={onWithdraw}
                    onDelete={onDelete}
                    onGenerateArtifacts={onGenerateArtifacts}
                    onAssign={onAssign}
                    onView={onView}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
